import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { WhatsappTemplateWithEnabled } from '@bandido/shared/types';
import { getWhatsappTemplateVariables } from '@frontend/features/whatsapp/constants/templateVariables';

type WhatsappTemplateConfigDialogProps = {
  open: boolean;
  template?: WhatsappTemplateWithEnabled;
  onClose: () => void;
  onSubmit: (values: { placeholderMappings: string[] }) => Promise<void>;
  isSubmitting?: boolean;
};

type FormValues = {
  placeholderMappings: string[];
};

const defaultValues: FormValues = {
  placeholderMappings: [],
};

export const WhatsappTemplateConfigDialog = ({
  open,
  template,
  onClose,
  onSubmit,
  isSubmitting = false,
}: WhatsappTemplateConfigDialogProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues,
  });

  const supportedVariables = useMemo(
    () => getWhatsappTemplateVariables(t),
    [t],
  );

  const detectedPlaceholders = useMemo(
    () => template?.data.match(/{{\d+}}/g) || [],
    [template?.data],
  );

  const normalizedPlaceholderMappings = useMemo(
    () =>
      Array.from({ length: detectedPlaceholders.length }, (_value, index) =>
        template?.placeholderMappings?.[index] ?? '',
      ),
    [detectedPlaceholders.length, template?.placeholderMappings],
  );

  useEffect(() => {
    if (!open) return;

    reset({
      placeholderMappings: normalizedPlaceholderMappings,
    });
  }, [open, normalizedPlaceholderMappings, reset]);

  const handleFormSubmit = async (values: FormValues) => {
    await onSubmit({
      placeholderMappings: detectedPlaceholders.map(
        (_placeholder, index) => values.placeholderMappings[index] ?? '',
      ),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        {t('whatsapp.settings.templates.dialog.title')}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="subtitle2">
              {t('whatsapp.settings.templates.dialog.templateText')}
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                px: 1.5,
                py: 1.5,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: 'grey.100',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {template?.data || ''}
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="subtitle2">
              {t('whatsapp.settings.templates.dialog.placeholderMappings')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('whatsapp.settings.templates.dialog.placeholderHelp')}
            </Typography>

            {detectedPlaceholders.map((placeholder, index) => (
              <Controller
                key={`${placeholder}-${index}`}
                name={`placeholderMappings.${index}` as const}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    select
                    fullWidth
                    size="small"
                    label={t(
                      'whatsapp.settings.templates.dialog.placeholderLabel',
                      { number: index + 1 },
                    )}
                    helperText={placeholder}
                  >
                    <MenuItem value="">
                      {t('whatsapp.settings.templates.dialog.unassigned')}
                    </MenuItem>
                    {supportedVariables.map((variable) => (
                      <MenuItem key={variable.token} value={variable.token}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="flex-start"
                          gap={0.25}
                        >
                          <Typography component="span" sx={{ fontFamily: 'monospace' }}>
                            {variable.token}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {variable.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            ))}

            {detectedPlaceholders.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('whatsapp.settings.templates.dialog.noPlaceholders')}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <LoadingButton loading={isSubmitting} type="submit" variant="contained">
            {t('common.save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
